import React, { useRef, Fragment } from "react";
import { observer } from "mobx-react-lite";
import {
  Input,
  SvgIcon,
  Tooltip,
  Range,
  round,
  useTargetListener,
  useOutsideClick,
  useDidMount,
  useBoolean,
  clsx,
} from "@gemlightbox/core-kit";
import { Webgl2New } from "src/image-editor-core";

import { useStores } from "src/hooks";
import { editMediaStore } from "../../edit-media.store";

import { ReactComponent as ArrowSVG } from "src/external-ts/assets/images/arrow-right-grey.svg";
import styles from "./scale-select.module.css";

export type ScaleSelectProps = {
  className?: string | undefined;
};

export const ScaleSelect: React.FC<ScaleSelectProps> = observer(({ className }) => {
  const { localeStore } = useStores();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const rangeWrapperRef = useRef<HTMLDivElement | null>(null);

  const isOpenBoolean = useBoolean(false);

  const { addTargetListener, removeTargetListener } = useTargetListener();

  const normalizedScale = editMediaStore.renderer.camera.scale;
  const percentageScale = round(normalizedScale * 100, 2) + "%";

  const handleTrigger = (e: React.MouseEvent | MouseEvent) => {
    e.stopPropagation();
    if (rangeWrapperRef.current?.contains(e.target as any)) return;
    isOpenBoolean.trigger();
  };

  const handleRangeChange = (value: number) => {
    editMediaStore.renderer.camera.setScale(value);
    editMediaStore.renderer.camera.centerViewportOnGlobalPosition(
      editMediaStore.renderer.canvasGlobalCenter,
    );
  };

  useDidMount(() => {
    addTargetListener("click", wrapperRef.current, handleTrigger);
    return removeTargetListener;
  });

  useOutsideClick([wrapperRef, tooltipRef], isOpenBoolean.setFalsy, {
    disable: !isOpenBoolean.value,
  });

  const arrowBtnStyles = clsx(styles.arrowBtn, { [styles.isOpen]: isOpenBoolean.value });

  return (
    <Fragment>
      <Input
        className={`${styles.scaleContainer} ${className}`}
        inputWrapperClassName={styles.scaleInputWrapper}
        appearance="primaryV2"
        childrenPos="before-input"
        value={percentageScale}
        forwardWrapperRef={wrapperRef}
        aria-haspopup={isOpenBoolean.value}
        disableError
        readOnly
        data-cy="scale-select"
      >
        <SvgIcon className={arrowBtnStyles} icon={ArrowSVG} onClick={handleTrigger} />
      </Input>
      <Tooltip
        className={styles.scaleRangeWrapper}
        position="bottomLeft"
        offsetY={4}
        target={wrapperRef}
        forwardRef={tooltipRef}
        onClose={isOpenBoolean.setValue}
        isOpen={isOpenBoolean.value}
      >
        <Range
          label={localeStore.t('["ar-media"]["media-editor"]["control-buttons"]["scale-select"]')}
          min={Webgl2New.CAMERA_MIN_SCALE}
          max={Webgl2New.CAMERA_MAX_SCALE}
          step={Webgl2New.CAMERA_SCALE_STEP}
          roundValue={2}
          value={normalizedScale}
          onChange={handleRangeChange}
          messageFormatter={percentageScale}
          showRangeValue
          disableError
        />
      </Tooltip>
    </Fragment>
  );
});

export default ScaleSelect;
